import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Avatar,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { watchersAPI } from '../../services/api';

// Types
export interface SlackUser {
  id: string;
  name: string;
  display_name: string;
  real_name?: string;
  profile_image?: string;
  is_active: boolean;
}

export interface SlackUserSelectorProps {
  selectedUsers: string[]; // Array of user IDs
  onUsersChange: (userIds: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  required?: boolean;
}

const SlackUserSelector: React.FC<SlackUserSelectorProps> = ({
  selectedUsers,
  onUsersChange,
  label = "Slack Users",
  placeholder = "Search for users...",
  helperText = "Type to search for Slack users",
  disabled = false,
  error = false,
  fullWidth = true,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SlackUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserObjects, setSelectedUserObjects] = useState<SlackUser[]>([]);

  // Search users function
  const searchUsers = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await watchersAPI.search(searchQuery);
      setOptions(result.watchers);
    } catch (error) {
      console.error('Error searching Slack users:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.trim().length >= 2) {
        searchUsers(inputValue);
      } else {
        setOptions([]);
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, searchUsers]);

  // Load user details for selected users when they change
  useEffect(() => {
    const loadSelectedUsers = async () => {
      if (selectedUsers.length === 0) {
        setSelectedUserObjects([]);
        return;
      }

      // Try to find users in current options first
      const foundUsers = selectedUsers
        .map(userId => options.find(user => user.id === userId))
        .filter(Boolean) as SlackUser[];

      // If we have all users in options, use them
      if (foundUsers.length === selectedUsers.length) {
        setSelectedUserObjects(foundUsers);
        return;
      }

      // Otherwise, we need to search for the missing users
      // For now, create placeholder objects for missing users
      const placeholderUsers = selectedUsers.map(userId => {
        const existingUser = foundUsers.find(user => user.id === userId);
        if (existingUser) return existingUser;
        
        return {
          id: userId,
          name: userId,
          display_name: `User ${userId}`,
          is_active: true,
        };
      });

      setSelectedUserObjects(placeholderUsers);
    };

    loadSelectedUsers();
  }, [selectedUsers, options]);

  const handleUserChange = (event: any, newValue: SlackUser[]) => {
    const userIds = newValue.map(user => user.id);
    setSelectedUserObjects(newValue);
    onUsersChange(userIds);
  };

  const formatOptionLabel = (option: SlackUser) => {
    return option.display_name || option.real_name || option.name;
  };

  const formatChipLabel = (user: SlackUser) => {
    return user.display_name || user.real_name || user.name;
  };

  return (
    <Autocomplete
      multiple
      options={options}
      value={selectedUserObjects}
      onChange={handleUserChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Only update input value for actual typing, ignore all resets
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
        // Ignore 'reset' and other reasons to keep user's input visible
      }}
      getOptionLabel={formatOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      filterOptions={(x) => x} // Disable built-in filtering since we do server-side search
      clearOnEscape={false} // Prevent clearing input on escape
      blurOnSelect={false} // Keep input focused after selection
      clearOnBlur={false} // Don't clear input when losing focus
      selectOnFocus
      handleHomeEndKeys
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((user, index) => (
          <Chip
            label={formatChipLabel(user)}
            avatar={
              user.profile_image ? (
                <Avatar 
                  src={user.profile_image} 
                  sx={{ width: 20, height: 20 }}
                  alt={user.display_name}
                />
              ) : (
                <Avatar sx={{ width: 20, height: 20 }}>
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Avatar>
              )
            }
            variant="outlined"
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Avatar
            src={option.profile_image}
            sx={{ width: 32, height: 32, mr: 2 }}
            alt={option.display_name}
          >
            {!option.profile_image && <PersonIcon />}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2">
              {option.display_name || option.real_name || option.name}
            </Typography>
            {option.real_name && option.display_name && option.real_name !== option.display_name && (
              <Typography variant="caption" color="text.secondary">
                {option.real_name}
              </Typography>
            )}
            {option.name && (
              <Typography variant="caption" color="text.secondary" display="block">
                @{option.name}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedUserObjects.length === 0 ? placeholder : ''}
          helperText={helperText}
          error={error}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        inputValue.length < 2 
          ? "Type at least 2 characters to search" 
          : loading 
            ? "Searching..." 
            : `No users found matching "${inputValue}"`
      }
    />
  );
};

export default SlackUserSelector; 